import { useState } from "react";

import { ApiError, fetchJson } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { formatDate, formatMoneyCents } from "./format";

// Phase 4b ar-and-dunning FR-19 / FR-20. Customer billing summary +
// Pay-now per invoice. Renders the same delinquency banner the
// PortalLayout already shows (the banner is one layer up); this page
// adds the per-invoice list + action.

interface BillingPayload {
  delinquent: boolean;
  outstanding_total_cents: number;
  unpaid: BillingInvoice[];
  paid: BillingInvoice[];
}

interface BillingInvoice {
  id: number;
  invoice_number: string;
  order_number: string;
  received_at: string | null;
  total_cents: number;
  outstanding_balance_cents: number;
  days_past_due: number;
  status: string;
  paid_at: string | null;
}

const BILLING_KEY = ["portal", "billing"] as const;

function useBilling() {
  return useQuery({
    queryKey: BILLING_KEY,
    queryFn: async () => fetchJson<BillingPayload>("/portal/account/billing.json")
  });
}

function usePayInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) =>
      fetchJson<{ ok: boolean; invoice_id: number; paid_at: string }>(
        `/portal/account/invoices/${id}/pay.json`,
        { method: "POST" }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BILLING_KEY });
      qc.invalidateQueries({ queryKey: ["portal", "dashboard"] });
    }
  });
}

export function BillingPage(): JSX.Element {
  const q = useBilling();
  const [error, setError] = useState<string | null>(null);
  const pay = usePayInvoice();

  if (q.isLoading) return <div className="p-4 text-sm text-zinc-600">Loading…</div>;
  if (!q.data) {
    return <div className="p-4 text-sm text-red-700">Couldn&rsquo;t load billing.</div>;
  }

  const data = q.data;

  function handlePay(id: number): void {
    setError(null);
    pay.mutate(id, {
      onError: (e) => {
        if (e instanceof ApiError) {
          const body = e.body as { reason?: string } | null;
          setError(reasonMessage(body?.reason));
        } else {
          setError(e.message);
        }
      }
    });
  }

  return (
    <section className="space-y-4 p-2">
      <header>
        <h1 className="text-lg font-semibold">Billing</h1>
        {/* The portal-wide delinquency banner lives in PortalLayout
            (rendered when `dashboard.delinquent` is true). Rendering
            a second banner here was confirmed as duplicate in round 1
            review — we surface the outstanding-total as a sub-header
            only so the customer sees the actionable number without
            the duplicated red box. Guarded on `> 0` to handle the
            race where the delinquent flag hasn't cleared yet but
            pay-now zeroed the balance (round-2 finding). */}
        {data.delinquent && data.outstanding_total_cents > 0 ? (
          <p className="mt-2 text-sm text-zinc-700" data-testid="billing-outstanding-total">
            Outstanding balance: <strong>{formatMoneyCents(data.outstanding_total_cents)}</strong>
          </p>
        ) : null}
      </header>

      {error ? (
        <p className="rounded border border-red-600 bg-red-50 p-2 text-sm text-red-700"
           data-testid="billing-error">
          {error}
        </p>
      ) : null}

      <section>
        <h2 className="text-base font-semibold">Outstanding invoices</h2>
        {data.unpaid.length === 0 ? (
          <p className="text-sm text-zinc-500">You&rsquo;re all caught up.</p>
        ) : (
          <ul className="space-y-2">
            {data.unpaid.map((inv) => (
              <li key={inv.id} className="rounded border border-zinc-200 p-3"
                  data-testid={`unpaid-${inv.id}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <div>
                    <p className="font-medium">{inv.invoice_number}</p>
                    <p className="text-xs text-zinc-600">
                      {inv.received_at ? formatDate(inv.received_at) : "—"} ·
                      {" "}{inv.days_past_due} day{inv.days_past_due === 1 ? "" : "s"} past due
                    </p>
                  </div>
                  <p className="text-base font-semibold">
                    {formatMoneyCents(inv.outstanding_balance_cents)}
                  </p>
                </div>
                <div className="mt-2 flex gap-2">
                  <button type="button"
                          onClick={() => handlePay(inv.id)}
                          disabled={pay.isPending}
                          className="rounded bg-zinc-900 px-3 py-1 text-sm text-white disabled:opacity-40"
                          data-testid={`pay-${inv.id}`}>
                    {pay.isPending ? "Charging…" : "Pay now"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {data.paid.length > 0 ? (
        <section>
          <h2 className="text-base font-semibold">Paid invoices</h2>
          <ul className="space-y-1 text-sm text-zinc-700">
            {data.paid.map((inv) => (
              <li key={inv.id} className="flex justify-between" data-testid={`paid-${inv.id}`}>
                <span>
                  {inv.invoice_number} · {inv.received_at ? formatDate(inv.received_at) : "—"}
                </span>
                <span>{formatMoneyCents(inv.total_cents)} · Paid</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </section>
  );
}

function reasonMessage(reason?: string): string {
  switch (reason) {
    case "no_payment_source":
    case "processor_unconfigured":
      // `processor_unconfigured` is a separate audit-log signal for
      // ops but the customer copy is identical: ops needs to set up
      // the order's processor before pay-now can charge.
      return "We don't have a card on file for this order. Please call us at (303) 435-4328.";
    case "capture_failed":
      // Adapter returns `capture_failed` for any upstream failure
      // (decline, timeout, gateway error). Don't claim "declined"
      // — softer wording covers all cases.
      return "We couldn't charge your card. Please try again, or call us at (303) 435-4328.";
    case "already_paid":
      return "This invoice is already paid.";
    case "written_off":
      return "This invoice has been written off.";
    default:
      return "We couldn't process the payment. Please try again or call us.";
  }
}
